import React, { useRef, useState } from "react";
import { Upload, Loader2, X, Star, GripVertical, FileText } from "lucide-react";
import { uploadFile } from "../../api";
import { useToast } from "../../hooks/use-toast";

const MAX_DIM = 2400;

// Resize large photos in-browser to web size (JPEG); PDFs/small files pass through.
export const shrinkImage = (file) => new Promise((resolve) => {
  if (!file.type.startsWith("image/") || file.type === "image/gif" || file.size < 600 * 1024) return resolve(file);
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    c.toBlob((b) => resolve(b && b.size < file.size ? new File([b], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

const isPdf = (u) => /\.pdf($|\?)/i.test(u);

const MediaDropzone = ({ label, value, onChange, accept = "image/*", testid, coverLabel }) => {
  const { toast } = useToast();
  const input = useRef(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(0);
  const [dragIdx, setDragIdx] = useState(null);
  const items = (value || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const set = (arr) => onChange(arr.join("\n"));

  const upload = async (files) => {
    files = files.filter((f) => accept.split(",").some((a) => a.endsWith("/*") ? f.type.startsWith(a.slice(0, -1)) : f.type === a));
    if (!files.length) return;
    setBusy(files.length);
    const urls = []; let failed = 0;
    for (const f of files) {
      try { urls.push(await uploadFile(await shrinkImage(f))); } catch { failed += 1; }
      setBusy((n) => n - 1);
    }
    if (urls.length) onChange([...(value || "").split("\n").map((s) => s.trim()).filter(Boolean), ...urls].join("\n"));
    toast({ title: failed ? `Uploaded ${urls.length}, ${failed} failed` : `Uploaded ${urls.length} file${urls.length > 1 ? "s" : ""}` });
  };

  const onTileDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    const arr = [...items]; const [m] = arr.splice(dragIdx, 1); arr.splice(i, 0, m); set(arr); setDragIdx(null);
  };

  return (
    <div data-testid={`${testid}-zone`}>
      <label className="text-[13px] font-500 text-[#6a6a6a] block mb-1.5">{label}</label>
      <div
        onDragOver={(e) => { if (dragIdx === null) { e.preventDefault(); setOver(true); } }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { setOver(false); if (dragIdx !== null) return; e.preventDefault(); upload(Array.from(e.dataTransfer.files || [])); }}
        onClick={() => input.current?.click()}
        data-testid={`${testid}-dropzone`}
        className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-5 text-center transition-colors ${over ? "border-[#b8935f] bg-[#faf6ef]" : "border-[#e2d8c8] hover:border-[#b8935f]"}`}
      >
        {busy > 0 ? <Loader2 size={18} className="animate-spin mx-auto text-[#b8935f]" /> : <Upload size={18} className="mx-auto text-[#b8935f]" />}
        <p className="text-[12px] text-[#6a6a6a] mt-1.5">{busy > 0 ? `Uploading ${busy}…` : "Drag files here or click to browse"}</p>
        <input ref={input} type="file" accept={accept} multiple hidden onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} data-testid={`${testid}-file-input`} />
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid={`${testid}-list`}>
          {items.map((url, i) => (
            <div key={url + i} draggable onDragStart={() => setDragIdx(i)} onDragEnd={() => setDragIdx(null)}
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onTileDrop(i); }}
              data-testid={`${testid}-item-${i}`}
              className={`relative group w-20 h-20 rounded-lg overflow-hidden border cursor-move ${i === 0 && coverLabel ? "border-[#b8935f] border-2" : "border-[#f0ece5]"} ${dragIdx === i ? "opacity-40" : ""}`}>
              {isPdf(url) ? <div className="w-full h-full flex items-center justify-center bg-[#faf6ef]"><FileText size={22} className="text-[#b8935f]" /></div>
                : <img src={url} alt="" className="w-full h-full object-cover pointer-events-none" />}
              {i === 0 && coverLabel && <span className="absolute bottom-0 inset-x-0 bg-[#b8935f] text-white text-[9px] text-center py-0.5">{coverLabel}</span>}
              <GripVertical size={12} className="absolute top-1 left-1 text-white drop-shadow opacity-0 group-hover:opacity-100" />
              <button type="button" onClick={() => set(items.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={11} /></button>
              {coverLabel && i > 0 && (
                <button type="button" title={`Make ${coverLabel.toLowerCase()}`} onClick={() => { const arr = [...items]; const [m] = arr.splice(i, 1); set([m, ...arr]); }} data-testid={`${testid}-cover-${i}`}
                  className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><Star size={11} /></button>
              )}
            </div>
          ))}
        </div>
      )}
      <details className="mt-1.5">
        <summary className="text-[11px] text-[#9a9a9a] cursor-pointer">Paste links instead</summary>
        <textarea placeholder="URLs, one per line" value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={`${testid}-urls`}
          className="w-full mt-1 rounded-lg border border-[#e2d8c8] px-3 py-2 text-[13px]" />
      </details>
    </div>
  );
};

export default MediaDropzone;
